/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPIHousien } from "../core/OpenAPI";
import { request as __request } from "../core/request";
import type {
  CondominiumPrefillResponse,
  FieldValuesResponse,
  PropertySuggestableField,
} from "../generated/types.gen";

/**
 * Values the agency already stored for one free-text property field
 * (plan §4). Read-only; scoped to the agency in the path.
 *
 * @param agencyId - The agency whose properties are read.
 * @param field - One of the closed list of suggestable fields.
 * @param q - Text to look for (fewer than two letters returns nothing).
 * @param limit - Maximum items (the server clamps it to 1..25).
 * @returns The matching values, most used first.
 */
export function getPropertyFieldValues(
  agencyId: string,
  field: PropertySuggestableField,
  q: string,
  limit = 10
): CancelablePromise<FieldValuesResponse> {
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agencies/{agencyId}/properties/field-values",
    path: { agencyId },
    query: { field, q, limit },
  });
}

/**
 * What the agency's other properties in a condominium have in common, for
 * the form to fill the fields that are still empty.
 *
 * @param agencyId - The agency whose properties are read.
 * @param name - The condominium name as chosen in the autocomplete.
 * @param excludePropertyId - The property being edited, so it does not vote for itself.
 * @returns The shared values, nested like the property.
 */
export function getCondominiumPrefill(
  agencyId: string,
  name: string,
  excludePropertyId?: string
): CancelablePromise<CondominiumPrefillResponse> {
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agencies/{agencyId}/properties/condominium-prefill",
    path: { agencyId },
    query: { name, exclude_property_id: excludePropertyId },
  });
}
